import { buildContentfulHooks } from 'services/ContentfulService/hooks/template'
import { parseProduct, Product, ProductWithChildren } from 'services/ContentfulService/models/product'
import { IProductFields } from 'generated/types/contentful'
import { ContentfulErrors } from 'services/ContentfulService/utils'
import { ObjectNotFoundError } from 'services/ContentfulService/errors'

const [useProductsHook, useProductsSSRHook, productFetcher] = buildContentfulHooks<Product, IProductFields>(
	'product',
	parseProduct
)

export const useProducts = useProductsHook
export const useProductsSSR = useProductsSSRHook

const sortProducts = (products: Product[]) => {
	return products.sort((a, b) => a.sort - b.sort)
}

const getProductById = async (id: string): Promise<Product> => {
	const constraints = [{ 'fields.id': id }]
	const products = await productFetcher(JSON.stringify(constraints), 1)
	const p1 = products.shift()
	if (p1) return p1
	throw new ObjectNotFoundError('product')
}

export const getProductBySlug = async (slug: string): Promise<Product> => {
	const constraints = [{ 'fields.slug': slug }]
	const products = await productFetcher(JSON.stringify(constraints), 1)
	const p1 = products.shift()
	if (p1) return p1
	const product2 = await getProductById(slug)
	if (product2) return product2
	throw ContentfulErrors.NotFound
}

const RootProductConstraint = { 'fields.parent[exists]': false }

export const getRootProducts = async (): Promise<Array<Product>> => {
	const constraints = [RootProductConstraint]
	const products = await productFetcher(JSON.stringify(constraints), 100)
	if (products) return sortProducts(products)
	throw new ObjectNotFoundError('rootProducts')
}

export const getAllProducts = async (): Promise<Array<Product>> => {
	const products = await productFetcher(JSON.stringify([]), 100)
	if (products) return sortProducts(products)
	throw ContentfulErrors.NotFound
}

export const getProductTree = async (): Promise<Array<ProductWithChildren>> => {
	const products = await getAllProducts()
	const topLevelProducts = products
		.filter(p => p.parent === undefined)
		.map(p => {
			return { ...p, children: [] } as ProductWithChildren
		})
	products.forEach(p => {
		if (p.parent) {
			topLevelProducts.find(tp => tp.id === p.parent)?.children?.push(p)
		}
	})
	return topLevelProducts
}
